/** CSV modal: template picker + save row. For Media Devoted | Created By Ciro Autiero */
import type { FC } from 'react';

import type { CsvExportTemplate } from '../useCsvExportTemplates';
import { CsvTemplateMenu } from './CsvTemplateMenu';
import { CsvExportTemplatesSaveRow } from './save-row';

const TPL_SIG = '\u0001';

interface CsvExportTemplatesSectionProps {
  templates: CsvExportTemplate[];
  exportColumnKeys: string[];
  editingTemplateId: string | null;
  canManageTemplates: boolean;
  templatesBusy: boolean;
  disabled: boolean;
  templateName: string;
  templateColor: string;
  saveAsDefault: boolean;
  onTemplateNameChange: (v: string) => void;
  onTemplateColorChange: (v: string) => void;
  onSaveAsDefaultChange: (v: boolean) => void;
  onApplyTemplate: (t: CsvExportTemplate) => void;
  onDeleteTemplate: (t: CsvExportTemplate) => boolean;
  onSaveTemplate: () => void;
  onUpdateTemplate: () => void;
}

export const CsvExportTemplatesSection: FC<CsvExportTemplatesSectionProps> = ({
  templates,
  exportColumnKeys,
  editingTemplateId,
  canManageTemplates,
  templatesBusy,
  disabled,
  templateName,
  templateColor,
  saveAsDefault,
  onTemplateNameChange,
  onTemplateColorChange,
  onSaveAsDefaultChange,
  onApplyTemplate,
  onDeleteTemplate,
  onSaveTemplate,
  onUpdateTemplate,
}) => {
  const blockDisabled = disabled || templatesBusy;
  const fieldLocked = blockDisabled || !canManageTemplates;
  const sig = exportColumnKeys.join(TPL_SIG);
  const sigMatchId = templates.find((t) => t.columns.join(TPL_SIG) === sig)?.id ?? null;
  /** Highlight only when current columns exactly match a saved template (not editingTemplateId alone). */
  const menuActiveId = sigMatchId;
  const canUpdate =
    canManageTemplates &&
    editingTemplateId !== null &&
    Boolean(templateName.trim()) &&
    !blockDisabled;

  return (
    <section className="space-y-3 rounded-lg border border-border/40 bg-muted/5 p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Templates</h4>
      {templates.length > 0 ? (
        <CsvTemplateMenu
          templates={templates}
          activeTemplateId={menuActiveId}
          disabled={disabled}
          showDelete={canManageTemplates && !blockDisabled}
          onApply={onApplyTemplate}
          onDelete={onDeleteTemplate}
        />
      ) : (
        <p className="text-xs text-muted-foreground">No saved templates yet.</p>
      )}
      <CsvExportTemplatesSaveRow
        fieldLocked={fieldLocked}
        canUpdate={canUpdate}
        canManageTemplates={canManageTemplates}
        templateName={templateName}
        templateColor={templateColor}
        saveAsDefault={saveAsDefault}
        onTemplateNameChange={onTemplateNameChange}
        onTemplateColorChange={onTemplateColorChange}
        onSaveAsDefaultChange={onSaveAsDefaultChange}
        onSaveTemplate={onSaveTemplate}
        onUpdateTemplate={onUpdateTemplate}
      />
    </section>
  );
};
